/** Field → messages map from a validation ProblemDetails (HTTP 400). */
export type FieldErrors = Record<string, string[]>;

/** RFC 9457 problem document as produced by the Orbita API. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  errors?: FieldErrors;
  [extension: string]: unknown;
}

/** Well-known client-side codes (server codes look like "tasks.not_found"). */
export const ClientErrorCode = {
  network: 'client.network_error',
  aborted: 'client.aborted',
  invalidResponse: 'client.invalid_response',
  unknown: 'client.unknown',
} as const;

/**
 * Error thrown by the API client for every non-2xx response or transport failure.
 * `code` is the ProblemDetails `code` extension when present, otherwise a generic `http.<status>`.
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly code: string;
  readonly title: string | undefined;
  readonly detail: string | undefined;
  readonly fieldErrors: FieldErrors;
  readonly problem: ProblemDetails | undefined;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    problem?: ProblemDetails;
    cause?: unknown;
  }) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.status = init.status;
    this.code = init.code;
    this.problem = init.problem;
    this.title = init.problem?.title;
    this.detail = init.problem?.detail;
    this.fieldErrors = normalizeFieldErrors(init.problem?.errors);
  }

  get isValidation(): boolean {
    return this.status === 400 && Object.keys(this.fieldErrors).length > 0;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isNetworkError(): boolean {
    return this.code === ClientErrorCode.network;
  }
  get isAborted(): boolean {
    return this.code === ClientErrorCode.aborted;
  }

  /** First message for a field (field names are matched case-insensitively, server uses camelCase). */
  fieldError(field: string): string | undefined {
    const key = Object.keys(this.fieldErrors).find((k) => k.toLowerCase() === field.toLowerCase());
    return key ? this.fieldErrors[key]?.[0] : undefined;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

function normalizeFieldErrors(errors: FieldErrors | undefined): FieldErrors {
  if (!errors || typeof errors !== 'object') return {};
  const result: FieldErrors = {};
  for (const [field, messages] of Object.entries(errors)) {
    // ASP.NET may send PascalCase or dotted paths ("Items[0].Title"); camel-case the first segment.
    const key = field.charAt(0).toLowerCase() + field.slice(1);
    result[key] = Array.isArray(messages) ? messages.map(String) : [String(messages)];
  }
  return result;
}

function isProblemDetails(value: unknown): value is ProblemDetails {
  return (
    typeof value === 'object' && value !== null && ('title' in value || 'status' in value || 'code' in value)
  );
}

/** Builds an ApiError from a failed fetch Response (body not yet consumed). */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let problem: ProblemDetails | undefined;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('json')) {
    try {
      const body: unknown = await response.json();
      if (isProblemDetails(body)) problem = body;
    } catch {
      // Malformed body: fall back to the status line.
    }
  }
  return apiErrorFromProblem(response.status, problem, response.statusText);
}

export function apiErrorFromProblem(
  status: number,
  problem: ProblemDetails | undefined,
  statusText = '',
): ApiError {
  const code = problem?.code ?? `http.${status}`;
  const message = problem?.detail ?? problem?.title ?? (statusText || `HTTP ${status}`);
  return new ApiError({ status, code, message, problem });
}
